import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import Button from '../components/Button'
import { reportScam } from '../hooks/useScamReport'
import {
  FiPhone,
  FiMail,
  FiMessageCircle,
  FiGlobe,
  FiUsers,
  FiHelpCircle,
  FiCheck,
  FiArrowLeft,
  FiCamera,
  FiX,
} from 'react-icons/fi'

export const Route = createFileRoute('/_auth/report-a-scam')({
  component: RouteComponent,
})

const scamTypes = [
  { id: 'phone', label: 'Phone call', description: 'Someone called me', icon: FiPhone },
  { id: 'email', label: 'Email', description: 'A suspicious email', icon: FiMail },
  { id: 'text', label: 'Text / SMS', description: 'A suspicious text', icon: FiMessageCircle },
  { id: 'website', label: 'Website', description: 'A fake or dodgy site', icon: FiGlobe },
  { id: 'social', label: 'Social media', description: 'Something on social', icon: FiUsers },
  { id: 'other', label: 'Other', description: 'Something else', icon: FiHelpCircle },
] as const

type ScamTypeId = (typeof scamTypes)[number]['id']

const placeholders: Record<ScamTypeId, string> = {
  phone: 'Who did they claim to be? What did they ask for? Did they pressure you to act quickly or share personal details?',
  email: 'What was the subject line? Who did it claim to be from? Did it include any links or attachments?',
  text: 'What did the message say? Did it include a link? Who did it claim to be from?',
  website: 'What was the website address? What was it pretending to be? How did you find it?',
  social: 'Which platform? What did the post or message say? Did they ask you to click a link or send money?',
  other: 'Describe what happened, including any details that might help us identify the scam.',
}

function RouteComponent() {
  const { user: clerkUser } = useUser()
  const userEmail = clerkUser?.primaryEmailAddress?.emailAddress || ''
  const userName = clerkUser?.firstName || clerkUser?.fullName || ''

  const [selectedType, setSelectedType] = useState<ScamTypeId | null>(null)
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const reportScamMutation = reportScam()
  const isLoading = reportScamMutation.status === 'pending'

  const selectedTypeLabel = scamTypes.find((t) => t.id === selectedType)?.label || ''

  useEffect(() => {
    if (!file) {
      setImagePreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setImagePreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedType || !description.trim()) return
    const fullDescription = `[${selectedTypeLabel}] ${description}`
    reportScamMutation.mutate(
      { description: fullDescription, file },
      { onSuccess: () => setSubmitted(true) },
    )
  }

  const handleFileSelect = (selected: File | undefined) => {
    if (!selected) return
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(selected.type)) return
    setFile(selected)
  }

  const handleReset = () => {
    setSelectedType(null)
    setDescription('')
    setFile(null)
    setSubmitted(false)
    reportScamMutation.reset()
  }

  // ── Success state ──────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <div className="mx-auto max-w-xl px-6 pb-16 pt-10 sm:px-8 sm:pt-14">
          <div className="rounded-2xl bg-white p-8 text-center shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-10">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <FiCheck className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Report received</h1>
            <p className="mx-auto mt-2 max-w-sm text-base leading-relaxed text-slate-500">
              Thank you for helping keep everyone safer. Our team will review your report and may reach out at <span className="font-medium text-slate-700">{userEmail}</span> if we need more details.
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-6 cursor-pointer text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
            >
              Submit another report
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Form state ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-4xl px-6 pb-16 pt-10 sm:px-8 sm:pt-14">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Report a scam
          </h1>
          <p className="mt-2 text-lg leading-relaxed text-slate-500">
            Help protect others by sharing what happened. Every report helps us spot new threats faster.
          </p>
        </div>

        <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
          {/* Reporting as bar */}
          <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4 sm:px-8">
            <span className="text-sm text-slate-400">Reporting as</span>
            <span className="text-sm font-medium text-slate-700">
              {userName ? `${userName} (${userEmail})` : userEmail}
            </span>
          </div>

          <div className="p-6 sm:p-8">
            {/* Step 1: Scam type */}
            <div className="mb-8">
              <p className="mb-3 text-base font-medium text-slate-700">What type of scam was it?</p>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {scamTypes.map((type) => {
                  const Icon = type.icon
                  const isActive = selectedType === type.id
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setSelectedType(type.id)}
                      className={`group flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-all ${
                        isActive
                          ? 'border-violet-400 bg-violet-50'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`mt-[3px] h-5 w-5 shrink-0 ${isActive ? 'text-violet-500' : 'text-slate-400'}`} />
                      <div>
                        <span className="text-sm font-medium text-slate-800">{type.label}</span>
                        <span className="mt-0.5 block text-sm leading-snug text-slate-400">
                          {type.description}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Step 2: Description */}
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="scam-description" className="mb-2 block text-base font-medium text-slate-700">
                  What happened?
                </label>
                <textarea
                  id="scam-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={selectedType ? placeholders[selectedType] : 'Select a scam type above, then describe what happened...'}
                  required
                  className="block w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100 min-h-[140px]"
                />
              </div>

              {/* Step 3: File upload */}
              <div className="mb-5">
                <p className="mb-2 text-base font-medium text-slate-700">
                  Screenshot or photo <span className="font-normal text-slate-400">(optional)</span>
                </p>
                {imagePreview ? (
                  <div className="relative rounded-xl border border-slate-200/80 bg-slate-50 p-3">
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="absolute right-2 top-2 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-colors hover:text-slate-600"
                      aria-label="Remove image"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                    <div className="flex justify-center">
                      <img src={imagePreview} alt="Attachment preview" className="max-h-[180px] rounded-lg" />
                    </div>
                    <p className="mt-1.5 text-center text-sm text-slate-400">{file?.name}</p>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 px-4 py-5 transition-colors hover:border-slate-300 hover:bg-slate-50/60">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files?.[0])}
                      disabled={isLoading}
                    />
                    <FiCamera className="mb-2 h-7 w-7 text-slate-300" />
                    <span className="text-base font-medium text-slate-600">Upload a screenshot</span>
                    <span className="mt-1 text-sm text-slate-400">JPEG, PNG, WebP, or GIF</span>
                  </label>
                )}
              </div>

              {reportScamMutation.isError && (
                <div className="mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {reportScamMutation.error?.message || 'Something went wrong. Please try again.'}
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <a
                  href="/help"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-slate-700"
                >
                  <FiArrowLeft className="h-3.5 w-3.5" />
                  Back to Help
                </a>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  isLoading={isLoading}
                  disabled={isLoading || !selectedType || !description.trim()}
                  className="rounded-xl"
                >
                  Submit report
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Reassurance note */}
        <p className="mt-4 text-center text-sm text-slate-400">
          Your report is confidential and helps protect the community.
        </p>
      </div>
    </div>
  )
}
